import { useEffect, useState, useCallback } from 'react'
import { useTelegram } from './use-telegram'

interface SafeAreaInset {
  top: number
  bottom: number
  left: number
  right: number
}

/**
 * Хук для полноэкранного режима (Bot API 8.0+)
 * 
 * Позволяет включить полноэкранный режим без заголовка Telegram.
 * Полезно для галерей изображений, видео и иммерсивного опыта.
 * 
 * Также предоставляет доступ к Safe Area Insets для устройств
 * с notch (iPhone X+) или Dynamic Island.
 * 
 * @example
 * ```tsx
 * const { 
 *   isFullscreen, 
 *   requestFullscreen, 
 *   exitFullscreen,
 *   safeAreaInset 
 * } = useFullscreen()
 * 
 * // Открыть галерею в полноэкранном режиме
 * const openGallery = () => {
 *   requestFullscreen()
 * }
 * 
 * // Стили с учётом Safe Area
 * const style = {
 *   paddingTop: safeAreaInset.top,
 *   paddingBottom: safeAreaInset.bottom,
 * }
 * ```
 */
export function useFullscreen() {
  const telegram = useTelegram()
  const [isFullscreen, setIsFullscreen] = useState(telegram.isFullscreen)
  const [safeAreaInset, setSafeAreaInset] = useState<SafeAreaInset>(telegram.safeAreaInset)
  const [contentSafeAreaInset, setContentSafeAreaInset] = useState<SafeAreaInset>(
    telegram.contentSafeAreaInset
  )

  // Слушаем изменения полноэкранного режима
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(telegram.isFullscreen)
    }

    const handleSafeAreaChange = () => {
      setSafeAreaInset(telegram.safeAreaInset)
      setContentSafeAreaInset(telegram.contentSafeAreaInset)
    }

    telegram.webApp.onEvent?.('fullscreenChanged', handleFullscreenChange)
    telegram.webApp.onEvent?.('safeAreaChanged', handleSafeAreaChange)
    telegram.webApp.onEvent?.('contentSafeAreaChanged', handleSafeAreaChange)

    return () => {
      telegram.webApp.offEvent?.('fullscreenChanged', handleFullscreenChange)
      telegram.webApp.offEvent?.('safeAreaChanged', handleSafeAreaChange)
      telegram.webApp.offEvent?.('contentSafeAreaChanged', handleSafeAreaChange)
    }
  }, [telegram.webApp])

  // Включить полноэкранный режим
  const requestFullscreen = useCallback(() => {
    telegram.hapticFeedback.impactOccurred('medium')
    telegram.requestFullscreen()
  }, [telegram])

  // Выключить полноэкранный режим
  const exitFullscreen = useCallback(() => {
    telegram.hapticFeedback.impactOccurred('light')
    telegram.exitFullscreen()
  }, [telegram])

  // Переключить полноэкранный режим
  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen()
    } else {
      requestFullscreen()
    }
  }, [isFullscreen, requestFullscreen, exitFullscreen])

  return {
    /** Текущее состояние полноэкранного режима */
    isFullscreen,
    /** Включить полноэкранный режим */
    requestFullscreen,
    /** Выключить полноэкранный режим */
    exitFullscreen,
    /** Переключить полноэкранный режим */
    toggleFullscreen,
    /** Safe Area отступы (для notch/Dynamic Island) */
    safeAreaInset,
    /** Safe Area отступы для контента */
    contentSafeAreaInset,
  }
}

